import React, { useState, useEffect, useRef  } from "react";
import { Box, Flex, HStack, Image, Button, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import StartSound from "../Images/StartSound.mp3";
import EndSound from "../Images/StartSound.mp3";

import TestModeIcon from "../Images/test-mode-image-icon.svg";
import { ChatBubbleLeftIcon, DocumentChartBarIcon, ArrowPathIcon, PaperAirplaneIcon, ShareIcon } from "@heroicons/react/24/outline";
import { a } from "./appColors";
import { solidButton, blueButton } from "./buttonStyles";
import { STANDE } from "../StandeClassroom/standeTokens";
import SolveHeaderBar from "./solve/SolveHeaderBar";


const SolveHeader = ({ subjectName, paperName, timeHour, timeMin, onTimeUp, setTestStarted, isUploading, uploadInProgressRef, paperMode, setPaperMode, onSubmitFullPaper, handleAnswerSubmit, answers, questions, canManageComments, isPlacingComment, onToggleCommentMode, commentCount, onReleaseFeedback, feedbackReleased, isReleasing, onOpenReport, hasReport, onRemark, isRemarking, onShare}) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(timeHour * 3600 + timeMin * 60);
  const [showAlert, setShowAlert] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [videoStream, setVideoStream] = useState(null);
  const recordedChunks = useRef([]);
  const videoRef = useRef(null);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const [showRemarkAlert, setShowRemarkAlert] = useState(false);
  const isPractice = paperMode === "PRACTICE";
  const isProctored = paperMode === "PROCTORED";
  const isClassroom = paperMode === "CLASSROOM";
  const isReview = paperMode === "REVIEW";
  const canSubmitFullPaper = paperMode !== "REVIEW";


  const headerButtonProps = {
    display: "inline-flex",
    px: { base: "8px", md: "14px" },
    py: { base: "6px", md: "8px" },
    alignItems: "center",
    gap: "6px",
    borderRadius: "9.677px",
    border: "1.21px solid",
    borderColor: a.border,
    color: a.text2,
    fontFamily: "Mona Sans",
    fontSize: { base: "11px", md: "12px" },
    fontWeight: 400,
    lineHeight: "normal",
    bg: a.surface,
    _hover: { bg: a.hover },
  };

  // Long labels are dropped on the narrowest screens; the icon carries the
  // meaning and the button stays tappable rather than pushing the row off
  // the edge.
  const labelOnWide = { display: { base: "none", sm: "inline" } };


  const startWebcamRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false, 
      });
      setVideoStream(stream);

      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.current.push(event.data);
        }
      };
      recorder.start();
      setMediaRecorder(recorder);
    } catch (err) {
      console.error("Error accessing webcam:", err);
    }
  };

  const stopWebcamRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks.current, { type: "video/webm" });
        const videoURL = URL.createObjectURL(blob);
        console.log("Video URL:", videoURL);
        recordedChunks.current = []; 
      };
    }
  
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop()); 
      setVideoStream(null); 
    }
  
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
  };
  
  useEffect(() => {
    return () => {
      stopWebcamRecording(); 
    };
  }, []);


  /** Leaving a paper: back if there is somewhere to go, else the dashboard.
   *  Blocked while a test is running, as it always was. */
  const handleBack = () => {
    if (isProctored) return;
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };

  const handleTestModeClick = () => {
    setPaperMode("PROCTORED");   // ✅ THIS is the real switch
    setTestStarted(true);
    startWebcamRecording();
    new Audio(StartSound).play();
    document.documentElement.requestFullscreen();
    window.history.pushState(null, null, window.location.href);
  };


  const handleCancelTestClick = (reason) => {
    if (uploadInProgressRef.current || isUploading) {
      console.log("Test mode prevented from exiting due to image upload.");
      return; 
    }
  
    setPaperMode("PRACTICE");
    setTestStarted(false);
    stopWebcamRecording();
    setTimeLeft(timeHour * 3600 + timeMin * 60);
  
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  
    if (reason === "tab-switch" || reason === "minimize") {
      setShowAlert(true);
    }
  };
  

  useEffect(() => {
    if (isProctored) {
      const preventNavigation = (e) => {
        e.preventDefault();
        e.returnValue = ""; 
      };

      const preventBackNavigation = () => {
        window.history.pushState(null, null, window.location.href); 
      };

      window.addEventListener("beforeunload", preventNavigation);
      window.addEventListener("popstate", preventBackNavigation);

      return () => {
        window.removeEventListener("beforeunload", preventNavigation);
        window.removeEventListener("popstate", preventBackNavigation);
        stopWebcamRecording();
      };
    }
  }, [isProctored]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else {
      onTimeUp(); 
    }
  }, [timeLeft, onTimeUp]);

  useEffect(() => {
    const handleTabChange = () => {
      if (isProctored && document.hidden && !isUploading) {
        handleCancelTestClick("tab-switch"); 
      }
  };

    document.addEventListener("visibilitychange", handleTabChange);

    return () => {
      document.removeEventListener("visibilitychange", handleTabChange);
    };
  }, [isProctored]);

  useEffect(() => {
    const handleVisibilityChange = () => {
        if (isProctored && document.hidden && !isUploading) {
            console.log("Tab minimized! Cancelling test...");
            handleCancelTestClick("minimize"); 
        }
    };

    const handleResize = () => {
        if (isProctored && (window.outerWidth < 800 || window.outerHeight < 600) && !isUploading) {
            console.log("Screen resized (potential minimize)! Cancelling test...");
            handleCancelTestClick("minimize");
        }
    };

    const handleEscapePress = (event) => {
      if (isProctored && event.key === "Escape" && !isUploading) {
          console.log("ESC key pressed! Cancelling test...");
          event.preventDefault(); 
          event.stopImmediatePropagation(); 
          handleCancelTestClick("esc-key");
      }
  };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleEscapePress, { once: true }); 

    return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("keydown", handleEscapePress);
    };

}, [isProctored, isUploading]);
 
  
useEffect(() => {


  const handleFullscreenChange = () => {
    if (isProctored && !document.fullscreenElement && !isUploading) {
      console.log("Exited fullscreen. Trying to re-enter...");
      
      document.documentElement.requestFullscreen()
        .catch((err) => {
          console.error("Error re-entering full-screen:", err);
        });
    }
  };
  
  document.addEventListener("fullscreenchange", handleFullscreenChange);
  
  return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  };
}, [isProctored, isUploading]);

  useEffect(() => {
    const handleResize = () => {
      if (isProctored && window.outerWidth < 800 && window.outerHeight < 600 && !isUploading) {
        handleCancelTestClick("minimize"); 
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isProctored]); 

  useEffect(() => {
    const disableShortcuts = (e) => {
      if (isProctored) {
        const restrictedKeys = ["F5", "F12", "Tab", "Escape"];
        if (
          restrictedKeys.includes(e.key) ||
          ((e.ctrlKey || e.metaKey) && ["t", "w", "r", "n"].includes(e.key))
        ) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener("keydown", disableShortcuts);

    return () => {
      window.removeEventListener("keydown", disableShortcuts);
    };
  }, [isProctored]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const handleSubmitTestEarly = async () => {
    setShowSubmitAlert(false); 
  
    console.log("Submitting test early...");
    console.log("Answers before submission:", answers);
    
    stopWebcamRecording(); 
    
    setTimeLeft(0);
    
    onTimeUp()

    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  
  
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  
    setPaperMode("PRACTICE");
    setTestStarted(false);
  
    console.log("Test submitted successfully.");
  };
  
  return (
    <>
      {/* In the flow, not pinned: the canvas below it is exactly the rest of
          the window, so nothing needs to be held above anything. */}
      <Box width="100%" flexShrink={0}>
        <SolveHeaderBar
          subjectName={subjectName}
          paperName={paperName}
          // An assignment with no time limit says nothing rather than
          // "0 hour 0 min".
          duration={
            timeHour || timeMin
              ? [timeHour ? `${timeHour} hour` : null, timeMin ? `${timeMin} min` : null]
                  .filter(Boolean)
                  .join(" ")
              : null
          }
          onBack={handleBack}
          backDisabled={isProctored}
        >
            {/* Sharing sits beside the mode's own action. Not during a
                proctored test, where nothing should leave the page. */}
            {onShare && !isProctored && (
              <Button {...headerButtonProps} onClick={onShare} aria-label="Share">
                <ShareIcon width="16px" height="16px" />
                <Text as="span" {...labelOnWide}>Share</Text>
              </Button>
            )}
          {isProctored && (
              <HStack align="left" spacing={4}>
                <Flex direction="column" align="start">
                  <Text fontSize="14px" fontWeight="400" color={a.textSub} maxWidth="400px">
                    You are in test mode. You can only submit questions. The test will auto-submit when time is over.
                  </Text>

                  <Flex
                    width="185px"
                    height="55px"
                    borderRadius="14px"
                    background="#070707"
                    alignItems="center"
                    justifyContent="center"
                    mt={4}
                  >
                    <Text fontSize="18px" fontWeight="400" color={a.blue}>
                      {formatTime(timeLeft)}
                    </Text>
                  </Flex>
                </Flex>

                <Button
                  padding="10px 24px"
                  borderRadius="14px"
                  background="#070707"
                  color="#FBFAFA"
                  fontSize="14px"
                  fontWeight="400"
                  width="145px"
                  _hover={{ background: "white", color: "#021A04" }}
                  onClick={() => handleCancelTestClick()}
                >
                  Cancel Test
                </Button>

                <Button
                  padding="10px 24px"
                  borderRadius="14px"
                  {...blueButton}
                  fontSize="14px"
                  fontWeight="400"
                  onClick={onSubmitFullPaper}
                >
                  Submit Paper
                </Button>
              </HStack>
            )}

            {isPractice && (
              <HStack spacing="12px">
                <Button
                  {...headerButtonProps}
                  onClick={handleTestModeClick}
                  aria-label="Test Mode"
                  // The one thing on this bar that starts something, so it
                  // carries the brand blue rather than another outline.
                  bg={STANDE.primaryBlue}
                  color={STANDE.black}
                  borderColor={STANDE.primaryBlue}
                  _hover={{ bg: STANDE.primaryBlue, filter: "brightness(0.94)" }}
                >
                  <Image
                    src={TestModeIcon}
                    boxSize="16px"
                    mr={{ base: 0, sm: "6px" }}
                    // Not the .app-nav-icon inversion: on blue the glyph is
                    // stande-black in both modes.
                    filter="none"
                  />
                  <Text as="span" {...labelOnWide}>Test Mode</Text>
                </Button>
              </HStack>
            )}
            {isClassroom && (
              <HStack spacing="12px">
                {/* The outlined pill from the design (222:186), not the
                    brand orange: submitting a paper is the expected end of
                    the session, not an alarm. */}
                <Button
                  {...headerButtonProps}
                  background={STANDE.cream}
                  color={STANDE.black}
                  border="1px solid"
                  borderColor={STANDE.black}
                  borderRadius="200px"
                  gap="6px"
                  fontWeight={500}
                  _hover={{ background: a.hover, color: STANDE.black }}
                  onClick={onSubmitFullPaper}
                >
                  Submit
                  <PaperAirplaneIcon width="18px" height="18px" />
                </Button>
              </HStack>
            )}
            {isReview && canManageComments && (
              <HStack spacing="12px" align="center">
                {isPlacingComment && (
                  <Text fontSize="12px" fontWeight="400" color="#535953">
                    Click anywhere on the paper to leave a comment
                  </Text>
                )}
                <Button
                  {...headerButtonProps}
                  {...(isPlacingComment
                    ? solidButton
                    : { background: a.surface, color: a.text2, _hover: { background: a.hover } })}
                  onClick={onToggleCommentMode}
                >
                  <ChatBubbleLeftIcon width="16px" height="16px" style={{ marginRight: "6px" }} />
                  {isPlacingComment ? "Cancel" : "Comments"}
                  {commentCount > 0 && !isPlacingComment ? ` (${commentCount})` : ""}
                </Button>

                <Button
                  {...headerButtonProps}
                  isDisabled={isRemarking}
                  isLoading={isRemarking}
                  loadingText="Re-marking"
                  onClick={() => setShowRemarkAlert(true)}
                >
                  <ArrowPathIcon width="16px" height="16px" style={{ marginRight: "6px" }} />
                  <Box as="span" {...labelOnWide}>Re-mark</Box>
                </Button>

                <Button
                  {...headerButtonProps}
                  onClick={onOpenReport}
                >
                  <DocumentChartBarIcon width="16px" height="16px" style={{ marginRight: "6px" }} />
                  {hasReport ? "View report" : "Generate report"}
                </Button>

                <Button
                  {...headerButtonProps}
                  isDisabled={feedbackReleased || isReleasing}
                  isLoading={isReleasing}
                  loadingText="Releasing"
                  {...(feedbackReleased
                    ? {
                        background: a.surfaceSubtle,
                        color: a.textSub,
                        border: "1.21px solid",
                        borderColor: a.border,
                        _hover: { background: a.surfaceSubtle, color: a.textSub },
                      }
                    : solidButton)}
                  onClick={onReleaseFeedback}
                >
                  {feedbackReleased ? "Feedback released ✓" : "Release feedback"}
                </Button>
              </HStack>
            )}
            {isReview && !canManageComments && hasReport && (
              <HStack spacing="12px" align="center">
                <Button {...headerButtonProps} onClick={onOpenReport}>
                  <DocumentChartBarIcon width="16px" height="16px" style={{ marginRight: "6px" }} />
                  View report
                </Button>
              </HStack>
            )}
        </SolveHeaderBar>
      </Box>

      {showAlert && (
        <Box
          display="inline-flex"
          height="243px"
          padding="46px 131px 119px 77px"
          flexDirection="column"
          alignItems="flex-start"
          gap="26px"
          borderRadius="14px"
          background="#070707"
          boxShadow="0px 0px 9.6px 0px rgba(0, 0, 0, 0.11)"
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="1000"
        >
          <Text color="#ffffff" fontSize="20px" fontWeight="500">
            Test Canceled
          </Text>
          <Text fontSize="14px" fontWeight="400" color="#ffffff">
            Your test was canceled because you minimized or resized the browser. Please restart if you wish to try again.
          </Text>
          <Button
            onClick={() => setShowAlert(false)}
            {...blueButton}
            borderRadius="8px"
            padding="8px 16px"
          >
            OK
          </Button>
        </Box>
      )}
      {showSubmitAlert && (
        <Box
          display="inline-flex"
          height="243px"
          padding="46px 131px 119px 77px"
          flexDirection="column"
          alignItems="flex-start"
          gap="26px"
          borderRadius="14px"
          background="#070707"
          boxShadow="0px 0px 9.6px 0px rgba(0, 0, 0, 0.11)"
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="1000"
        >
          <Text color="#ffffff" fontSize="20px" fontWeight="500">
            Submit Test Early?
          </Text>
          <Text fontSize="14px" fontWeight="400" color="#ffffff">
            Are you sure you want to submit your test early? You cannot undo this action.
          </Text>
          <Flex gap="8px" mt="24px">
            <Button
              {...blueButton}
              borderRadius="8px"
              padding="8px 16px"
              onClick={() => {
                stopWebcamRecording();  // ✅ Turn off webcam
                onTimeUp();  // ✅ Submit test
                setShowSubmitAlert(false);  // ✅ Hide the alert
              }}
            >
              Yes, Submit
            </Button>
            <Button
              backgroundColor="transparent"
              color="#ffffff"
              borderRadius="8px"
              padding="8px 16px"
              border="1px solid white"
              onClick={() => setShowSubmitAlert(false)}
            >
              No, Cancel
            </Button>
          </Flex>
        </Box>
      )}

      {showRemarkAlert && (
        <Box
          position="fixed"
          inset="0"
          background="rgba(0, 0, 0, 0.45)"
          zIndex="1000"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px="20px"
          onClick={() => setShowRemarkAlert(false)}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="14px"
            width="100%"
            maxWidth="420px"
            padding="28px"
            borderRadius="14px"
            background="#070707"
            boxShadow="0px 0px 9.6px 0px rgba(0, 0, 0, 0.11)"
            onClick={(e) => e.stopPropagation()}
          >
            <Text color="#ffffff" fontSize="20px" fontWeight="500">
              Mark this submission again?
            </Text>
            <Text fontSize="14px" fontWeight="400" color="#ffffff" lineHeight="1.5">
              Every answer is marked again from what the student submitted. The
              marks and feedback you can see now are replaced, and feedback goes
              back to unreleased so you can check it before the student sees it.
            </Text>
            <Flex gap="8px" mt="10px">
              <Button
                {...blueButton}
                borderRadius="8px"
                padding="8px 16px"
                onClick={() => {
                  setShowRemarkAlert(false);
                  onRemark();
                }}
              >
                Yes, re-mark
              </Button>
              <Button
                backgroundColor="transparent"
                color="#ffffff"
                borderRadius="8px"
                padding="8px 16px"
                border="1px solid white"
                _hover={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                onClick={() => setShowRemarkAlert(false)}
              >
                Cancel
              </Button>
            </Flex>
          </Box>
        </Box>
      )}

      {/* Video Preview Box */}
      {isProctored && (
        <Box
          position="fixed"
          bottom="10px"
          left="10px"
          width="200px"
          height="150px"
          borderRadius="10px"
          overflow="hidden"
          boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
          zIndex="1000"
        >
          <video
            ref={videoRef}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "10px",
            }}
          />
        </Box>
      )}

    </>
  );
};

export default SolveHeader;